import React, { useEffect, useMemo, useRef, useState } from 'react';
import P from 'prop-types';
import { v4 as uuid } from 'uuid';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import CtaButton from './lib/cta-button';
import { group, wrapper } from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import UniversalContainer from '../universal-container';
import { addWebProtocolIfNeeded } from '../../lib/add-web-protocol';
import styles from './universal-button.module.scss';
import { useCustomButtonColor } from './lib/use-custom-button-color';

const defaultProps = {
  buttonText: 'Button',
  buttonType: 'button',
};

const UniversalButton = ({
  buttonURL,
  buttonText,
  textColorOptions,
  titleColorOptions,
  ctaExtraText,
  alignment,
  outputSample,
  buttonContainer,
  includeContainer,
  customButtonColorValues,
  customButtonActiveColor,
  customButtonHoverColor,
  customButtonTextActiveColor,
  customButtonTextHoverColor,
  ...ctaButtonProps
}) => {
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL && addWebProtocolIfNeeded(buttonURL);
  const nodeId = uuid();

  const buttonProps = useMemo(
    () => ({
      impressionId: nodeId,
      ...ctaButtonProps,
      buttonText:
        !buttonText && outputSample ? defaultProps.buttonText : buttonText,
      buttonType:
        !ctaButtonProps?.buttonType ||
        (ctaButtonProps?.buttonText === 'none' && outputSample)
          ? defaultProps.buttonType
          : ctaButtonProps?.buttonType,
    }),
    [buttonText, ctaButtonProps, outputSample, nodeId]
  );

  const buttonContainerProps = {
    ...buttonContainer,
    alignment: {
      ...buttonContainer?.alignment,
      type: 'flex',
      direction: 'column',
    },
  };

  const buttonRef = useRef();
  const [disabled, setDisabled] = useState(false);

  const enableValidation =
    (ctaButtonProps?.enableQueryStringFieldValidation &&
      ctaButtonProps?.queryStringFieldValidationName) ||
    false;

  useCustomButtonColor({
    buttonRef,
    customButtonActiveColor,
    customButtonColorValues,
    customButtonHoverColor,
    customButtonTextActiveColor,
    customButtonTextHoverColor,
  });

  const searchParams = useSearchParams();
  const validationQueryParamFound = searchParams.get(
    ctaButtonProps?.queryStringFieldValidationName
  );

  useEffect(() => {
    if (!enableValidation) {
      return;
    }
    setDisabled(!validationQueryParamFound);
  }, [enableValidation, validationQueryParamFound]);

  const isButtonDisabled = enableValidation
    ? disabled || !validationQueryParamFound
    : false;

  return (
    <div
      ref={buttonRef}
      style={{ textAlign: alignment }}
      className={classNames({ [styles.button]: customButtonColorValues })}
    >
      {includeContainer ? (
        <UniversalContainer
          {...buttonContainerProps}
          className={styles.container}
        >
          <CtaButton
            buttonURL={modifiedUrl}
            buttonClass={classNames('cta__button', styles.cta__button)}
            {...buttonProps}
            disabled={isButtonDisabled}
          />
          <UniversalText
            {...ctaExtraText}
            defaultOptions={{ text: { textType: 'text' } }}
          />
        </UniversalContainer>
      ) : (
        <React.Fragment>
          <CtaButton
            buttonURL={modifiedUrl}
            buttonClass="cta__button"
            {...buttonProps}
            disabled={isButtonDisabled}
          />
          <UniversalText
            {...ctaExtraText}
            defaultOptions={{ text: { textType: 'text' } }}
          />
        </React.Fragment>
      )}
    </div>
  );
};

UniversalButton.displayName = 'UniversalButton';

UniversalButton.propTypes = {
  ...CtaButton.propTypes,
  componentName: P.string,
  alignment: P.oneOf(['left', 'center', 'right']),
  children: P.node,
};

UniversalButton.defaultProps = {
  ...CtaButton.defaultProps,
  componentName: 'universal-button',
  alignment: 'left',
  children: null,
};

/**
 * @param {*} componentDocument
 * @param {string} fieldName
 * @param {string} fieldLabel
 * @param {Array<'displayStyle'|'disabled'|'loading'|'loadingMessage'|'alignment'>} allowedControlsList List of controls to be shown in the editor
 * @param {object} options
 * @returns
 */
UniversalButton.getEditingConfig = (
  componentDocument,
  fieldName = 'universalButton',
  fieldLabel = 'Universal Button',
  allowedControlsList = [],
  options = {
    disableGroup: false,
    includeContainer: false,
  }
) => {
  const editorPanelOptions = [
    ...CtaButton.getEditingConfig(
      componentDocument,
      fieldName,
      allowedControlsList
    ),
    ...(options.includeContainer
      ? [
          ...UniversalContainer.getEditingConfig(
            componentDocument,
            'Container',
            ['padding', 'margin', 'alignment', 'width'],
            `${fieldName}.buttonContainer`,
            { disableGroup: true, includeContainer: true }
          ),
        ]
      : []),
  ];
  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default UniversalButton;
